import { apiFetch } from "../api/client";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

function formatDate(value) {
  if (!value) return "N/A";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusClass(value) {
  const normalized = String(value || "unknown")
    .toLowerCase()
    .replaceAll("_", "-");

  return `soc-status soc-status-${normalized}`;
}

function priorityClass(value) {
  return `soc-priority soc-priority-${String(
    value || "P4"
  ).toLowerCase()}`;
}

function SOCAnalystConsolePage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [approvalActionId, setApprovalActionId] = useState(null);

  const loadSummary = useCallback(async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      const response = await apiFetch(`/soc-console/summary`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Unable to load the SOC analyst console."
        );
      }

      setSummary(data);
    } catch (loadError) {
      console.error("SOC console load error:", loadError);
      setError(loadError.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();

    const interval = window.setInterval(() => {
      loadSummary(true);
    }, 30000);

    return () => window.clearInterval(interval);
  }, [loadSummary]);

  const decideApproval = async (approval, decision) => {
    const runId = approval.run_id;
    const actionType = approval.action_type || "unknown action";
    const isApproval = decision === "approve";

    let reason = "";

    if (isApproval) {
      const confirmed = window.confirm(
        `Approve "${formatLabel(actionType)}" for ${
          approval.hostname || approval.device_uuid || "this endpoint"
        }?\n\nThis may disrupt the endpoint or its network access.`
      );

      if (!confirmed) return;

      reason = window.prompt(
        "Enter the approval reason:",
        `Approved ${formatLabel(actionType)} after SOC review.`
      );

      if (reason === null) return;
    } else {
      reason = window.prompt(
        "Enter the rejection reason:",
        "Disruptive action rejected after SOC review."
      );

      if (reason === null) return;
    }

    try {
      setApprovalActionId(approval.approval_id);

      const username =
        localStorage.getItem("username") || "SOC Administrator";

      const role =
        localStorage.getItem("role") || "SOC Manager";

      const endpoint = isApproval
        ? `/soar/runs/${runId}/approve`
        : `/soar/runs/${runId}/reject`;

      const payload = isApproval
        ? {
            approved_by: username,
            approved_by_role: role,
            reason: reason.trim(),
          }
        : {
            rejected_by: username,
            rejected_by_role: role,
            reason: reason.trim(),
          };

      const response = await apiFetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            `Unable to ${isApproval ? "approve" : "reject"} action.`
        );
      }

      await loadSummary(true);

      window.alert(
        isApproval
          ? "SOAR action approved successfully."
          : "SOAR action rejected successfully."
      );
    } catch (decisionError) {
      console.error("SOAR approval decision error:", decisionError);
      window.alert(decisionError.message);
    } finally {
      setApprovalActionId(null);
    }
  };

  if (loading) {
    return (
      <div className="soc-console-loading">
        Loading Wireless Press SOC operations...
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="soc-console-error">
        <h3>SOC Console Unavailable</h3>
        <p>{error}</p>
        <button type="button" onClick={() => loadSummary()}>
          Retry
        </button>
      </div>
    );
  }

  const incidentMetrics = summary?.incident_metrics || {};
  const soarMetrics = summary?.soar_metrics || {};
  const edrMetrics = summary?.edr_metrics || {};

  const incidents = Array.isArray(summary?.incidents)
    ? summary.incidents
    : [];

  const activeRuns = Array.isArray(summary?.active_soar_runs)
    ? summary.active_soar_runs
    : [];

  const pendingApprovals = Array.isArray(summary?.pending_approvals)
    ? summary.pending_approvals
    : [];

  const recentActions = Array.isArray(summary?.recent_edr_actions)
    ? summary.recent_edr_actions
    : [];

  const mitreCoverage = Array.isArray(summary?.mitre_coverage)
    ? summary.mitre_coverage
    : [];

  return (
    <div className="soc-console-page">
      <section className="soc-command-header">
        <div>
          <div className="soc-eyebrow">
            24×7 SECURITY OPERATIONS CENTER
          </div>

          <h2>Analyst Command Console</h2>

          <p>
            Live incident response, endpoint operations, automation,
            and ATT&amp;CK visibility across managed environments.
          </p>
        </div>

        <div className="soc-command-actions">
          <span className="soc-live-indicator">
            <span />
            Live Operations
          </span>

          <button
            type="button"
            onClick={() => loadSummary(true)}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh Console"}
          </button>
        </div>
      </section>

      {error && (
        <div className="soc-inline-warning">
          Last refresh failed: {error}
        </div>
      )}

      <section className="soc-metric-grid">
        <article className="soc-metric-card">
          <span>Open Incidents</span>
          <strong>{incidentMetrics.open_incidents || 0}</strong>
          <small>
            {incidentMetrics.unassigned_incidents || 0} unassigned
          </small>
        </article>

        <article className="soc-metric-card soc-metric-critical">
          <span>P1 Critical</span>
          <strong>{incidentMetrics.p1_incidents || 0}</strong>
          <small>Immediate response required</small>
        </article>

        <article className="soc-metric-card soc-metric-high">
          <span>P2 High</span>
          <strong>{incidentMetrics.p2_incidents || 0}</strong>
          <small>Priority investigation queue</small>
        </article>

        <article className="soc-metric-card">
          <span>Active SOAR Runs</span>
          <strong>{soarMetrics.active_runs || 0}</strong>
          <small>
            {soarMetrics.pending_approvals || 0} awaiting approval
          </small>
        </article>

        <article className="soc-metric-card">
          <span>Active EDR Actions</span>
          <strong>{edrMetrics.active_actions || 0}</strong>
          <small>
            {edrMetrics.pending_actions || 0} pending approval
          </small>
        </article>

        <article className="soc-metric-card">
          <span>Triage Queue</span>
          <strong>{incidentMetrics.triage_incidents || 0}</strong>
          <small>Cases currently under triage</small>
        </article>
      </section>

      <section className="soc-console-grid">
        <article className="soc-console-panel soc-incidents-panel">
          <div className="soc-panel-heading">
            <div>
              <span>CASE MANAGEMENT</span>
              <h3>Priority Incident Queue</h3>
            </div>

            <Link to="/incidents">View All Incidents</Link>
          </div>

          <div className="soc-table-wrapper">
            <table className="soc-console-table">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Incident</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>Assignment</th>
                  <th>Updated</th>
                </tr>
              </thead>

              <tbody>
                {incidents.length > 0 ? (
                  incidents.map((incident) => (
                    <tr key={incident.id}>
                      <td>
                        <span className={priorityClass(incident.priority)}>
                          {incident.priority || "P4"}
                        </span>
                      </td>

                      <td>
                        <Link
                          className="soc-incident-link"
                          to={`/incidents/${incident.id}`}
                        >
                          <strong>{incident.incident_number}</strong>
                          <span>{incident.title}</span>
                        </Link>
                      </td>

                      <td>
                        <strong>{incident.hostname || "Unknown"}</strong>
                        <span className="soc-cell-subtext">
                          {incident.company_name || "Unassigned company"}
                        </span>
                      </td>

                      <td>
                        <span className={statusClass(incident.status)}>
                          {formatLabel(incident.status)}
                        </span>
                      </td>

                      <td>
                        {incident.assigned_team || "Unassigned"}
                      </td>

                      <td>{formatDate(incident.updated_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="soc-empty-row">
                      No active incidents.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="soc-console-panel">
          <div className="soc-panel-heading">
            <div>
              <span>AUTOMATION</span>
              <h3>Active SOAR Runs</h3>
            </div>
          </div>

          <div className="soc-activity-list">
            {activeRuns.length > 0 ? (
              activeRuns.map((run) => (
                <div className="soc-activity-item" key={run.id}>
                  <div className="soc-activity-main">
                    <strong>{run.playbook_name}</strong>
                    <span>
                      {run.incident_number || `Incident ${run.incident_id}`}
                    </span>
                  </div>

                  <div className="soc-activity-meta">
                    <span className={statusClass(run.status)}>
                      {formatLabel(run.status)}
                    </span>
                    <small>Step {run.current_step_order || 0}</small>
                  </div>
                </div>
              ))
            ) : (
              <div className="soc-empty-state">
                No active SOAR playbook runs.
              </div>
            )}
          </div>
        </article>

        <article className="soc-console-panel soc-approval-panel">
          <div className="soc-panel-heading">
            <div>
              <span>HUMAN AUTHORIZATION</span>
              <h3>Pending SOAR Approvals</h3>
            </div>

            <div className="soc-approval-count">
              {pendingApprovals.length}
            </div>
          </div>

          <div className="soc-approval-list">
            {pendingApprovals.length > 0 ? (
              pendingApprovals.map((approval) => {
                const busy =
                  approvalActionId === approval.approval_id;

                return (
                  <div
                    className="soc-approval-item"
                    key={approval.approval_id}
                  >
                    <div className="soc-approval-warning">
                      Approval required before executing a potentially
                      disruptive endpoint action.
                    </div>

                    <div className="soc-approval-header">
                      <div>
                        <strong>
                          {formatLabel(approval.action_type)}
                        </strong>

                        <span>{approval.step_name}</span>
                      </div>

                      <span className="soc-status soc-status-waiting-for-approval">
                        Pending
                      </span>
                    </div>

                    <div className="soc-approval-details">
                      <div>
                        <span>Incident</span>
                        <strong>
                          {approval.incident_number || "N/A"}
                        </strong>
                      </div>

                      <div>
                        <span>Endpoint</span>
                        <strong>
                          {approval.hostname ||
                            approval.device_uuid ||
                            "Unknown"}
                        </strong>
                      </div>

                      <div>
                        <span>Playbook</span>
                        <strong>{approval.playbook_name}</strong>
                      </div>

                      <div>
                        <span>Requested</span>
                        <strong>
                          {formatDate(approval.requested_at)}
                        </strong>
                      </div>
                    </div>

                    {approval.incident_id && (
                      <Link
                        className="soc-approval-incident-link"
                        to={`/incidents/${approval.incident_id}`}
                      >
                        Open Incident Investigation
                      </Link>
                    )}

                    <div className="soc-approval-actions">
                      <button
                        type="button"
                        className="soc-approval-reject"
                        disabled={busy}
                        onClick={() =>
                          decideApproval(approval, "reject")
                        }
                      >
                        {busy ? "Processing..." : "Reject"}
                      </button>

                      <button
                        type="button"
                        className="soc-approval-approve"
                        disabled={busy}
                        onClick={() =>
                          decideApproval(approval, "approve")
                        }
                      >
                        {busy ? "Processing..." : "Approve Action"}
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="soc-empty-state">
                No SOAR actions are waiting for approval.
              </div>
            )}
          </div>
        </article>

        <article className="soc-console-panel">
          <div className="soc-panel-heading">
            <div>
              <span>ENDPOINT RESPONSE</span>
              <h3>Recent EDR Actions</h3>
            </div>
          </div>

          <div className="soc-activity-list">
            {recentActions.length > 0 ? (
              recentActions.slice(0, 10).map((action) => (
                <div className="soc-activity-item" key={action.id}>
                  <div className="soc-activity-main">
                    <strong>{formatLabel(action.action_type)}</strong>
                    <span>
                      {action.hostname || action.device_uuid}
                    </span>

                    {action.incident_number && (
                      <small>{action.incident_number}</small>
                    )}
                  </div>

                  <div className="soc-activity-meta">
                    <span className={statusClass(action.status)}>
                      {formatLabel(action.status)}
                    </span>
                    <small>{formatDate(action.requested_at)}</small>
                  </div>
                </div>
              ))
            ) : (
              <div className="soc-empty-state">
                No endpoint actions recorded.
              </div>
            )}
          </div>
        </article>

        <article className="soc-console-panel">
          <div className="soc-panel-heading">
            <div>
              <span>THREAT COVERAGE</span>
              <h3>MITRE ATT&amp;CK Coverage</h3>
            </div>
          </div>

          <div className="soc-mitre-list">
            {mitreCoverage.length > 0 ? (
              mitreCoverage.map((technique) => (
                <div
                  className="soc-mitre-item"
                  key={technique.technique_id}
                >
                  <div>
                    <strong>{technique.technique_id}</strong>
                    <span>Detection coverage</span>
                  </div>

                  <div className="soc-mitre-count">
                    {technique.detection_count}
                  </div>
                </div>
              ))
            ) : (
              <div className="soc-empty-state">
                No ATT&amp;CK coverage mappings found.
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="soc-health-strip">
        <div>
          <span>Completed SOAR Runs</span>
          <strong>{soarMetrics.completed_runs || 0}</strong>
        </div>

        <div>
          <span>Failed SOAR Runs</span>
          <strong>{soarMetrics.failed_runs || 0}</strong>
        </div>

        <div>
          <span>Completed EDR Actions</span>
          <strong>{edrMetrics.completed_actions || 0}</strong>
        </div>

        <div>
          <span>Failed EDR Actions</span>
          <strong>{edrMetrics.failed_actions || 0}</strong>
        </div>
      </section>
    </div>
  );
}

export default SOCAnalystConsolePage;
